/**
 * Разбор пользовательского текста (ТЗ FR-EN-18).
 *
 * Слова приводятся к нижнему регистру, знаки по краям снимаются, частота
 * считается внутри принесённого текста. Разбор идёт на устройстве и никуда не
 * отправляется: единицы режима носят приставку `custom:` и на сервер не
 * уходят — идентификатор такой единицы сам по себе выдал бы содержание
 * текста (ТЗ FR-EN-20). Из текста берутся
 * только слова и их частота внутри него: этого достаточно, чтобы собрать
 * упражнение теми же правилами, что и из частотного словаря.
 *
 * Слово с символом, которого нет в выбранной раскладке, отбрасывается: набрать
 * его пользователь не сможет, и упражнение из него было бы издевательством.
 */

import { getLayout, supportsCharacter } from '@type2x/keyboard-layouts';

import type { CustomTextSnapshot, CustomWord, ParsedText } from '../contracts/custom-text.js';
import { MAX_TEXT_LENGTH, MAX_WORD_LENGTH, MIN_WORDS } from '../contracts/custom-text.js';

/** Разделители слов: всё, что не буква, не цифра и не дефис внутри слова. */
const SEPARATORS = /[^\p{L}\p{Nd}'’-]+/u;

/** Знаки, которые не могут стоять по краям слова. */
const EDGE_MARKS = /^['’-]+|['’-]+$/gu;

/** Приводит слово к виду учебной единицы: нижний регистр без краевых знаков. */
function normalize(word: string): string {
  return word.toLowerCase().replace(EDGE_MARKS, '');
}

/**
 * Разбирает текст на слова с частотами.
 *
 * @param text текст, принесённый пользователем;
 * @param snapshot снимок навыка: из него берётся раскладка.
 */
export function parseCustomText(text: string, snapshot: CustomTextSnapshot): ParsedText {
  const layout = getLayout(snapshot.layoutId, snapshot.platform);
  const counts = new Map<string, number>();
  const unsupported: string[] = [];
  const unsupportedSeen = new Set<string>();

  let rejectedWords = 0;
  let total = 0;

  for (const raw of text.slice(0, MAX_TEXT_LENGTH).split(SEPARATORS)) {
    const word = normalize(raw);
    if (word === '' || [...word].length > MAX_WORD_LENGTH) continue;

    const missing = [...word].filter((character) => !supportsCharacter(layout, character));
    if (missing.length > 0) {
      rejectedWords += 1;
      for (const character of missing) {
        if (unsupportedSeen.has(character)) continue;
        unsupportedSeen.add(character);
        unsupported.push(character);
      }
      continue;
    }

    counts.set(word, (counts.get(word) ?? 0) + 1);
    total += 1;
  }

  const words: CustomWord[] = [...counts.entries()]
    .map(([word, count]) => ({ text: word, count, frequency: total === 0 ? 0 : count / total }))
    // Частое в тексте слово важнее редкого: пользователь принёс материал
    // ради того, что печатает часто.
    .sort((left, right) => right.count - left.count || left.text.localeCompare(right.text));

  return {
    words,
    rejectedWords,
    unsupportedCharacters: unsupported,
    usable: words.length >= MIN_WORDS,
  };
}
